import {
  useRef,
  type PropsWithChildren,
  type MouseEvent,
  type PointerEvent,
} from 'react';

import {
  container,
  tooltipOverlay,
  tooltipOverlayFull,
  consumerLoadingStyle,
} from './GaugeLayoutOverlay.sty.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { LoaderOverlay } from '../../../core/components/loader-overlay/LoaderOverlay.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useTooltipActions } from '../../../core/components/overlay-tooltip/useTooltipActions.js';
import { useToolbarHandlerRef } from '../../../core/components/toolbar/hooks/useToolbarHandlerRef.js';
import {
  ARC_INNER_RADIUS_PERCENTAGE,
  ARC_OUTER_RADIUS_PERCENTAGE,
} from '../../constants.js';
import { useGaugeChartTooltip } from '../../contexts/GaugeChartTooltipContext.js';
import { useGaugeChartTooltipOverlayBounds } from '../../hooks/useGaugeChartTooltipOverlayBounds.js';
import { isInGaugeHitArea } from '../../utils/is-in-gauge-hit-area.js';
import {
  GaugeChartToolbar,
  type GaugeChartToolbarProps,
} from '../GaugeChartLayout/GaugeChartToolbar.js';
import { GaugeOverlayTooltip } from '../GaugeChartTooltip/GaugeOverlayTooltip.js';

const isPointerEventInHitArea = (
  event: MouseEvent<HTMLDivElement>,
  overlayBounds: { width: number } | undefined,
): boolean => {
  if (!overlayBounds) {
    return true;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  const arcOuterRadius = overlayBounds.width / 2;
  const arcInnerRadius =
    arcOuterRadius *
    (ARC_INNER_RADIUS_PERCENTAGE / ARC_OUTER_RADIUS_PERCENTAGE);
  return isInGaugeHitArea(
    event.clientX - rect.left - arcOuterRadius,
    event.clientY - rect.top - arcOuterRadius,
    arcOuterRadius,
    arcInnerRadius,
  );
};

interface GaugeChartOverlayProps {
  value: number;
  color: string;
  showLoading?: boolean;
  intents: GaugeChartToolbarProps['intents'];
  toolbar: GaugeChartToolbarProps['toolbar'];
}

export const GaugeChartOverlay = (
  props: PropsWithChildren<GaugeChartOverlayProps>,
) => {
  const { children, value, color, intents, toolbar, showLoading } = props;
  const overlay = useOverlay();
  const overlayBounds = useGaugeChartTooltipOverlayBounds();
  const { name: tooltipName, hidden: tooltipHidden } = useGaugeChartTooltip();
  const { hover, togglePin, outOfBounds } = useTooltipActions();
  const toolbarHandlerRef = useToolbarHandlerRef();
  const isTouchInteractionRef = useRef(false);

  const data = {
    geometry: 'meterbar' as const,
    series: [{ name: tooltipName ?? 'Value', value, color }],
  };

  const overlayClassName = overlayBounds
    ? tooltipOverlay
    : `${tooltipOverlay} ${tooltipOverlayFull}`;
  const overlayStyle = overlayBounds
    ? {
        top: overlayBounds.top,
        left: overlayBounds.left,
        width: overlayBounds.width,
        height: overlayBounds.height,
      }
    : undefined;

  const handleClick = (
    event: MouseEvent<HTMLDivElement> | PointerEvent<HTMLDivElement>,
  ) => {
    // The pinned tooltip is portaled but still bubbles its events here
    // through the React tree; only toggle for taps on the gauge itself,
    // otherwise interacting with the tooltip (e.g. series actions)
    // would close it. On touch the tooltip is already toggled in
    // onPointerDown, and iOS may still emit a (sometimes delayed)
    // synthetic click, so skip it here to avoid toggling back off.
    if (
      isTouchInteractionRef.current ||
      event.target !== event.currentTarget ||
      !isPointerEventInHitArea(event, overlayBounds)
    ) {
      return;
    }
    togglePin(event, data);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (
      event.target === event.currentTarget &&
      !isPointerEventInHitArea(event, overlayBounds)
    ) {
      outOfBounds();
      return;
    }
    hover(event, data);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    isTouchInteractionRef.current = event.pointerType === 'touch';
    toolbarHandlerRef.current.handleFocus();
    // On touch devices the first tap reveals the toolbar, which makes
    // iOS suppress the following synthetic click. Toggle the tooltip
    // here so both appear on the first tap — but only for taps on the
    // gauge itself, not on the portaled tooltip bubbling its events up.
    if (
      isTouchInteractionRef.current &&
      event.target === event.currentTarget &&
      isPointerEventInHitArea(event, overlayBounds)
    ) {
      togglePin(event, data);
    }
  };

  return (
    <Flex
      ref={overlay.overlayRef}
      data-testid="gauge-overlay-wrapper"
      className={container({ showLoading })}
    >
      <GaugeChartToolbar
        intents={intents}
        toolbar={toolbar}
        handlerRef={toolbarHandlerRef}
      />

      {!showLoading && (
        <Flex
          className={overlayClassName}
          onPointerMove={handlePointerMove}
          onPointerLeave={outOfBounds}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          style={overlayStyle}
        >
          {!tooltipHidden && (
            <GaugeOverlayTooltip value={value} color={color} />
          )}
        </Flex>
      )}

      {children}
      {showLoading ? (
        <LoaderOverlay
          gridArea="1/1/2/2"
          consumerStyle={consumerLoadingStyle}
        />
      ) : null}
    </Flex>
  );
};

GaugeChartOverlay['displayName'] = 'GaugeChartOverlay';
